import React, { useEffect, useState, useRef } from 'react';
import { ZoomInIcon, ZoomOutIcon, RotateCwIcon, DownloadIcon, PrinterIcon, SearchIcon, MoreHorizontalIcon, MaximizeIcon, MinimizeIcon, MoveHorizontalIcon, MoveVerticalIcon } from 'lucide-react';
import { InvoiceDocument } from '../types/invoice';
interface ImageViewerPanelProps {
  document: InvoiceDocument;
}
type FitMode = 'none' | 'width' | 'height';
export function ImageViewerPanel({
  document
}: ImageViewerPanelProps) {
  const [fitMode, setFitMode] = useState<FitMode>('width');
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [calculatedScale, setCalculatedScale] = useState(1);
  // Calculate scale for fit height mode
  useEffect(() => {
    if (fitMode === 'height' && containerRef.current && contentRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (!containerRef.current || !contentRef.current) return;
        const containerHeight = containerRef.current.clientHeight - 32; // Account for padding
        const contentHeight = contentRef.current.scrollHeight;
        // Calculate scale to fit height
        const scale = containerHeight / contentHeight;
        console.log('Fit Height Calculation:', {
          containerHeight,
          contentHeight,
          scale
        });
        setCalculatedScale(scale);
      });
    } else {
      setCalculatedScale(1);
    }
  }, [fitMode, document]);
  const handleFitToWidth = () => {
    setFitMode('width');
    setZoom(100);
  };
  const handleFitToHeight = () => {
    setFitMode('height');
    setZoom(100);
  };
  const handleZoomIn = () => {
    setFitMode('none');
    setZoom(prev => Math.min(prev + 25, 300));
  };
  const handleZoomOut = () => {
    setFitMode('none');
    setZoom(prev => Math.max(prev - 25, 25));
  };
  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };
  // Determine container classes based on fit mode
  const getContainerClasses = () => {
    if (fitMode === 'width') {
      return 'w-full';
    } else if (fitMode === 'height') {
      return '';
    }
    return '';
  };
  const getWrapperClasses = () => {
    if (fitMode === 'width') {
      return 'w-full';
    } else if (fitMode === 'height') {
      return 'h-full flex items-start justify-center py-4';
    }
    return 'flex justify-center';
  };
  const getContentStyle = () => {
    const scale = fitMode === 'height' ? calculatedScale : zoom / 100;
    const baseStyle: React.CSSProperties = {
      transform: `rotate(${rotation}deg) scale(${scale})`,
      transformOrigin: 'center center',
      transition: 'transform 0.2s ease-out'
    };
    return baseStyle;
  };
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-full overflow-hidden" data-id="element-5946">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50" data-id="element-5947">
        <div className="flex items-center gap-2" data-id="element-5948">
          <button onClick={handleZoomOut} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Zoom Out" data-id="element-5949">
            <ZoomOutIcon className="w-4 h-4 text-gray-600" data-id="element-5950" />
          </button>
          <span className="text-xs text-gray-600 font-medium min-w-[3rem] text-center" data-id="element-5951">
            {zoom}%
          </span>
          <button onClick={handleZoomIn} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Zoom In" data-id="element-5952">
            <ZoomInIcon className="w-4 h-4 text-gray-600" data-id="element-5953" />
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-5954" />
          <button onClick={handleFitToWidth} className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${fitMode === 'width' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200 text-gray-700'}`} title="Fit to Width" data-id="element-5955">
            <MoveHorizontalIcon className="w-3.5 h-3.5" data-id="element-5956" />
            Fit Width
          </button>
          <button onClick={handleFitToHeight} className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${fitMode === 'height' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200 text-gray-700'}`} title="Fit to Height" data-id="element-5957">
            <MoveVerticalIcon className="w-3.5 h-3.5" data-id="element-5958" />
            Fit Height
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-5959" />
          <button onClick={handleRotate} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Rotate" data-id="element-5960">
            <RotateCwIcon className="w-4 h-4 text-gray-600" data-id="element-5961" />
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-5962" />
          <span className="text-sm text-gray-600" data-id="element-5963">1 of 1</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-5964">
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-5965">
            <SearchIcon className="w-4 h-4 text-gray-600" data-id="element-5966" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-5967">
            <DownloadIcon className="w-4 h-4 text-gray-600" data-id="element-5968" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-5969">
            <PrinterIcon className="w-4 h-4 text-gray-600" data-id="element-5970" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-5971">
            <MoreHorizontalIcon className="w-4 h-4 text-gray-600" data-id="element-5972" />
          </button>
        </div>
      </div>

      {/* Invoice Preview */}
      <div ref={containerRef} className="flex-1 bg-gray-100 overflow-auto" data-id="element-5973">
        <div className={`min-h-full ${getWrapperClasses()}`} data-id="element-5974">
          <div ref={contentRef} className={`bg-white shadow-lg ${getContainerClasses()}`} style={getContentStyle()} data-id="element-5975">
            {/* Invoice Header - FROM VENDOR */}
            <div className="p-8 border-b border-gray-200" data-id="element-5976">
              <div className="flex justify-between items-start" data-id="element-5977">
                {/* Vendor (Supplier) Details - Left Side */}
                <div data-id="element-5978">
                  <div className="font-bold text-2xl mb-2" data-id="element-5979">
                    {document.supplier}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-5980">
                    <div data-id="element-5981">123 Supplier Street</div>
                    <div data-id="element-5982">Sydney NSW 2000</div>
                    <div data-id="element-5983">Australia</div>
                    <div className="mt-2" data-id="element-5984">Phone: (02) 9999 8888</div>
                    <div data-id="element-5985">ABN: 12 345 678 901</div>
                  </div>
                </div>

                {/* Invoice Title - Right Side */}
                <div className="text-right" data-id="element-5986">
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5987">
                    INVOICE
                  </div>
                  <div className="text-sm font-medium text-gray-700" data-id="element-5988">
                    {document.invoiceNumber}
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="p-8" data-id="element-5989">
              {/* Bill To - Customer (Abbott) */}
              <div className="mb-6 bg-gray-50 p-4 rounded border border-gray-200" data-id="element-5990">
                <div className="text-xs font-bold text-gray-700 mb-2 uppercase" data-id="element-5991">
                  Bill To
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-5992">
                  Abbott Australasia Pty Ltd
                </div>
                <div className="text-xs text-gray-600" data-id="element-5993">
                  <div data-id="element-5994">Level 4, 32-34 Lord Street</div>
                  <div data-id="element-5995">Botany NSW 2019</div>
                  <div data-id="element-5996">Australia</div>
                  <div className="mt-2" data-id="element-5997">Phone: (02) 9384 3000</div>
                  <div data-id="element-5998">ABN: 95 000 180 389</div>
                </div>
              </div>

              {/* Invoice Info */}
              <div className="grid grid-cols-4 gap-4 mb-6 text-xs" data-id="element-5999">
                <div data-id="element-6000">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6001">
                    Invoice Date
                  </div>
                  <div className="font-medium" data-id="element-6002">{document.invoiceDate}</div>
                </div>
                <div data-id="element-6003">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6004">
                    Due Date
                  </div>
                  <div className="font-medium" data-id="element-6005">{document.dueDate}</div>
                </div>
                <div data-id="element-6006">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6007">Terms</div>
                  <div className="font-medium" data-id="element-6008">Net 30 Days</div>
                </div>
                {document.poNumber && <div data-id="element-6009">
                    <div className="text-gray-600 mb-1 font-semibold" data-id="element-6010">
                      PO Number
                    </div>
                    <div className="font-medium text-blue-600" data-id="element-6011">
                      {document.poNumber}
                    </div>
                  </div>}
              </div>

              {/* Line Items */}
              <table className="w-full mb-6 text-xs" data-id="element-6012">
                <thead className="bg-gray-100 border-b-2 border-gray-300" data-id="element-6013">
                  <tr data-id="element-6014">
                    <th className="text-left py-2 px-2 font-semibold" data-id="element-6015">
                      Item Code
                    </th>
                    <th className="text-left py-2 px-2 font-semibold" data-id="element-6016">
                      Description
                    </th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6017">Qty</th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6018">
                      Unit Price
                    </th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6019">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-6020">
                  {document.lineItems.map((item, index) => <tr key={index} className="border-b border-gray-200" data-id="element-6021">
                      <td className="py-3 px-2 font-mono text-xs" data-id="element-6022">
                        {item.itemCode}
                      </td>
                      <td className="py-3 px-2" data-id="element-6023">{item.description}</td>
                      <td className="text-right py-3 px-2" data-id="element-6024">{item.quantity}</td>
                      <td className="text-right py-3 px-2" data-id="element-6025">
                        ${item.unitPrice.toFixed(2)}
                      </td>
                      <td className="text-right py-3 px-2 font-medium" data-id="element-6026">
                        ${item.total.toFixed(2)}
                      </td>
                    </tr>)}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end" data-id="element-6027">
                <div className="w-64 text-xs" data-id="element-6028">
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-6029">
                    <span data-id="element-6030">Subtotal:</span>
                    <span className="font-medium" data-id="element-6031">
                      ${document.subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-6032">
                    <span data-id="element-6033">GST (10%):</span>
                    <span className="font-medium" data-id="element-6034">
                      ${document.taxAmt.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-t-2 border-gray-900 font-bold text-base" data-id="element-6035">
                    <span data-id="element-6036">Total {document.currency}:</span>
                    <span data-id="element-6037">${document.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-6038">
                <div className="text-xs" data-id="element-6039">
                  <div className="font-semibold mb-2" data-id="element-6040">Payment Details:</div>
                  <div className="text-gray-600" data-id="element-6041">
                    <div data-id="element-6042">Bank: Commonwealth Bank</div>
                    <div data-id="element-6043">BSB: 062 000</div>
                    <div data-id="element-6044">Account: 1234 5678</div>
                    <div className="mt-2" data-id="element-6045">
                      Please include invoice number in payment reference
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200 text-center" data-id="element-6046">
                <div className="text-xs text-gray-500" data-id="element-6047">
                  Thank you for your business!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}